import React, { useState } from 'react';
import {
  Button,
  Modal,
  TextField,
  TEXT_FIELD_APPEARANCE_PILL,
  TEXT_FIELD_SIZE_LG,
} from '@aibuild/ui';

export interface ProcessRenameDialogProps {
  open: boolean;
  onClose: () => void;
  /** Current process name — used to seed the input on open. */
  currentName: string;
  /**
   * Called with the trimmed new name when the user commits. May be async —
   * the consumer drives loading state via {@link isRenaming}. The dialog
   * does NOT auto-close on submit; the parent closes it after a successful
   * mutation so a server-side error can leave the dialog mounted with the
   * user's draft intact.
   */
  onSubmit: (newName: string) => void | Promise<void>;
  isRenaming?: boolean;
  dataTestId?: string;
}

const ProcessRenameDialog: React.FC<ProcessRenameDialogProps> = ({
  open,
  onClose,
  currentName,
  onSubmit,
  isRenaming = false,
  dataTestId = 'process-rename-dialog',
}) => {
  const [draft, setDraft] = useState(currentName);
  // Re-seed the draft each time the dialog reopens. We use the
  // render-time-derived-state pattern (per React 19 docs) instead of an
  // effect because `react-hooks/set-state-in-effect` flags that pattern
  // and the in-render version is strictly faster (one render instead of
  // mount + setState + re-render).
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(currentName);
  }

  const trimmed = draft.trim();
  const canSave = !!trimmed && trimmed !== currentName && !isRenaming;

  const commit = () => {
    if (!canSave) return;
    // Fire-and-forget: the parent surfaces success / failure through
    // `isRenaming` and the snackbar pipeline. `Promise.resolve` normalises
    // the `void | Promise<void>` return type so we can chain `.catch` safely
    // even when the consumer hands us a sync function (no rejection to
    // swallow, but we still need to discard any returned promise without
    // using `void` as an operator).
    Promise.resolve(onSubmit(trimmed)).catch(() => undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  };

  return (
    <Modal
      open={open}
      onClose={isRenaming ? () => undefined : onClose}
      title="Rename Process"
      size="sm"
      dataTestId={dataTestId}
      hideCloseButton={isRenaming}
      disableEscape={isRenaming}
      disableBackdropClose={isRenaming}
      footer={
        <Button
          variant="filled"
          onClick={commit}
          disabled={!canSave}
          loading={isRenaming}
          dataTestId={`${dataTestId}__save`}
        >
          Save
        </Button>
      }
    >
      <TextField
        appearance={TEXT_FIELD_APPEARANCE_PILL}
        size={TEXT_FIELD_SIZE_LG}
        stretch
        autoFocus
        value={draft}
        onChange={setDraft}
        onKeyDown={handleKeyDown}
        disabled={isRenaming}
        aria-label="Process name"
        dataTestId={`${dataTestId}__field`}
      />
    </Modal>
  );
};

export default ProcessRenameDialog;
